import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Collapse,
  Grid2,
  IconButton,
  Paper,
  Switch,
  useTheme,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import HistoryIcon from "@mui/icons-material/History";
import { observer, useLocalObservable } from "mobx-react";
import { runInAction } from "mobx";
import CustomTypography from "../../../../../views/components/common/form/CustomTypography";
import AppButton from "../../../../../views/components/common/buttons/AppButton";
import CustomTextField from "../../../../../views/components/common/textFields/TextField";
import RichTextEditor from "../../../../../views/components/common/textEditor/RichTextEditor";
import AppSearchDropdownMulti from "../../../../../views/components/common/filter/AppSearchDropdownMulti";
import ConfirmationPopup from "../../../../../views/components/common/pop-up/ConfirmationPopup";
import ImageGalleryPopup from "../../../../../views/components/common/pop-up/ImageGalleryPopup";
import GradientDivider from "../../../../../views/components/common/divider/GradientDivider";
import ProductSkuBuilder from "./ProductSkuBuilder";
import ProductVariantImagesEditor from "./ProductVariantImagesEditor";
import { useDebounce } from "../../../../../views/components/common/hooks/Debounce";
import { useStores } from "../../../use-stores";
import { useNotification } from "../../../../../utils/NotificationsContext";
import { EmployeeRoles } from "../../../../../models/Employees/EmployeeRoles";
import { CatalogProductStore } from "../../../stores/CatalogProductStore";
import { CategoryStore } from "../../../stores/CategoryStore";
import VendorAPI from "../../../api/VendorAPI";
import ProductAPI from "../../../api/ProductAPI";
import ImageAPI from "../../../api/ImageAPI";
import { FilterValue } from "../../../../../models/Filters/FilterValue";
import {
  CreateProductInput,
  UpdateImagesInput,
  UpdateVariantsInput,
  asFlag,
  getApiErrorMessage,
  variantOptionDetailsKey,
} from "../../../../../models/CatalogProducts/types";
import { SortType } from "../../../../../models/SortType";
import ProductIcon from "../../../../../assets/productIcon.png";
import NotesIcon from "../../../../../assets/notesIcon.png";
import StatusIcon from "../../../../../assets/pricetagicon.png";
import RupeeIcon from "../../../../../assets/rupeeicon.png";
import BusinessIcon from "../../../../../assets/business_building.png";
import SkuIcon from "../../../../../assets/liningicon.png";
import ImageIcon from "../../../../../assets/imageicon.png";
import UploadIcon from "../../../../../assets/uploadimg.png";
import SourcingIcon from "../../../../../assets/sourcingIcon.png";
import SearchIcon from "../../../../../assets/searchIcon.png";
import TagIcon from "../../../../../assets/hashtagIcon.png";
import EyeIcon from "../../../../../assets/eyeframe.png";
import SkuCopyRow from "../../../../../views/components/common/SkuCopyRow";
import RichTextCopyButton from "../../../../../views/components/common/RichTextCopyButton";
import MediaAssetsUpload from "../../../../../views/components/common/upload/MediaAssetsUpload";
import PodProductPsdMappingsCard from "../../components/product/PodProductPsdMappingsCard";
import { activeCatalogSkus } from "../../components/swagstore/mockups/catalogSkuChoices";
import MockupConfigAPI from "../../../api/MockupConfigAPI";
import ProductActivityLogsDrawer from "./ProductActivityLogsDrawer";
import {
  formatAmount,
  formatDate,
} from "../../../../../views/components/common/functions/formatDate";

type SectionKey =
  | "core"
  | "sku"
  | "pod"
  | "variantImages"
  | "media"
  | "psd"
  | "sourcing"
  | "categories"
  | "seo";

type ProductMediaImage = UpdateImagesInput["images"][number] & {
  localFile?: File;
};

interface GalleryImage {
  id?: number;
  src: string;
  alt?: string;
}

const ProductDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const theme = useTheme();
  const { userStore, podCatalogStore } = useStores();
  const { showError, showSuccess } = useNotification();
  const catalogProductStore = useRef(new CatalogProductStore()).current;
  const categoryStore = useRef(new CategoryStore()).current;
  const vendorAPI = useRef(new VendorAPI()).current;
  const productAPI = useRef(new ProductAPI()).current;
  const mockupConfigAPI = useRef(new MockupConfigAPI()).current;
  const colors = theme.palette.customColors;

  const isNew = !id || id === "new";
  const productId = isNew ? null : Number(id);
  const [activityLogsDrawerOpen, setActivityLogsDrawerOpen] = useState(false);
  const [psdLoadingOptions, setPsdLoadingOptions] = useState(false);
  const [psdUploading, setPsdUploading] = useState(false);
  const [purchaseInsightsOpen, setPurchaseInsightsOpen] = useState(true);

  const canDeleteItemSku = userStore.hasPermission(
    EmployeeRoles.DELETE_ITEM_SKU,
  );
  const canEdit = userStore.hasPermission(EmployeeRoles.EDIT_PRODUCT);
  const canEditSourcing = userStore.hasPermission(
    EmployeeRoles.EDIT_PRODUCT_SOURCING_DETAILS,
  );
  const canEditSeo = userStore.hasPermission(
    EmployeeRoles.EDIT_PRODUCT_SEO_DETAILS,
  );
  const canEditPrinting = userStore.hasPermission(
    EmployeeRoles.EDIT_PRODUCT_SOURCING_PRINTING_DETAILS,
  );

  const page = useLocalObservable(() => ({
    loading: !isNew,
    saving: false,
    deleteOpen: false,
    openSections: {
      core: true,
      sku: false,
      pod: false,
      variantImages: false,
      media: false,
      psd: false,
      sourcing: false,
      categories: false,
      seo: false,
    } as Record<SectionKey, boolean>,
    // core
    name: "",
    desc: "",
    costPrice: "",
    sellingPrice: "",
    sku: "",
    brandName: "",
    dimensions: "",
    sizes: "",
    capacity: "",
    colors: "",
    priority: "0",
    display: 0,
    printOnDemand: 0,
    isBestSelling: 0,
    length: "",
    breadth: "",
    height: "",
    weight: "",
    categoryId: null as number | null,
    categoryName: "",
    preferredVendorId: null as number | null,
    preferredVendorName: "",
    // Saved variants (from server) + pending additions from the option picker
    savedVariants: [] as UpdateVariantsInput["variants"],
    pendingVariants: [] as UpdateVariantsInput["variants"],
    variantSearch: "",
    variantsBuilderResetKey: 0,
    // Print on demand
    podSellingPrice: "",
    podProductionTime: "",
    // media
    sizeChartUrl: "",
    selectedSizeChartFile: null as File | null,
    images: [] as ProductMediaImage[],
    setSelectedSizeChartFile(file: File | null) {
      this.selectedSizeChartFile = file;
    },
    clearSelectedSizeChartFile() {
      this.selectedSizeChartFile = null;
    },
    removeSizeChartImage() {
      this.sizeChartUrl = "";
    },
    // image gallery preview
    hoveredImageKey: null as string | null,
    galleryOpen: false,
    galleryImages: [] as GalleryImage[],
    galleryTitle: "Product Images",
    galleryInitialIndex: 0,
    setHoveredImageKey(key: string | null) {
      this.hoveredImageKey = key;
    },
    openGallery(
      images: GalleryImage[],
      initialIndex = 0,
      title = "Product Images",
    ) {
      try {
        const valid = images.filter((img) => Boolean(img.src));
        if (!valid.length) return;
        this.galleryImages = valid;
        this.galleryInitialIndex = Math.min(
          Math.max(initialIndex, 0),
          valid.length - 1,
        );
        this.galleryTitle = title;
        this.galleryOpen = true;
      } catch {
        // Ignore gallery open failures; page remains usable.
      }
    },
    closeGallery() {
      this.galleryOpen = false;
      this.galleryImages = [];
      this.galleryInitialIndex = 0;
    },
    // sourcing
    zohoId: "",
    hsnCode: "",
    gstPercentage: "",
    fastProcurement: 0,
    preferredPrintingTypeId: null as number | null,
    preferredPrintingTypeName: "",
    vendorIds: [] as number[],
    printingTypeIds: [] as number[],
    // categories extra
    extraCategoryIds: [] as number[],
    // seo
    seoTitle: "",
    seoDescription: "",
    // pickers
    categoryList: [] as FilterValue[],
    vendorList: [] as FilterValue[],
    printingTypeList: [] as FilterValue[],
    categorySearch: "",
    vendorSearch: "",
    toggleSection(key: SectionKey) {
      this.openSections[key] = !this.openSections[key];
    },
    hydratePod() {
      const pod = catalogProductStore.productPod;
      if (!pod) {
        this.podSellingPrice = "";
        this.podProductionTime = "";
        return;
      }
      this.podSellingPrice =
        pod.pod_selling_price != null
          ? String(pod.pod_selling_price)
          : pod.swageazy_selling_price != null
            ? String(pod.swageazy_selling_price)
            : "";
      this.podProductionTime =
        pod.production_time != null ? String(pod.production_time) : "";
    },
    hydrateFromDetail() {
      const d = catalogProductStore.productDetail;
      if (!d) return;
      this.name = d.name ?? "";
      this.desc = d.desc ?? "";
      this.costPrice = String(d.cost_price ?? "");
      this.sellingPrice = String(d.selling_price ?? "");
      this.sku = d.sku ?? "";
      this.brandName = d.brand_name ?? "";
      this.dimensions = d.dimensions ?? "";
      this.sizes = d.sizes ?? "";
      this.capacity = d.capacity ?? "";
      this.colors = d.colors ?? "";
      this.priority = String(d.priority ?? 0);
      this.display = asFlag(d.display);
      this.printOnDemand = asFlag(d.print_on_demand);
      this.isBestSelling = asFlag(d.is_best_selling);
      this.length = d.length != null ? String(d.length) : "";
      this.breadth = d.breadth != null ? String(d.breadth) : "";
      this.height = d.height != null ? String(d.height) : "";
      this.weight = d.weight != null ? String(d.weight) : "";
      this.categoryId = d.category_id || null;
      this.categoryName = d.category_name ?? "";
      this.preferredVendorId = d.preferred_vendor_id;
      this.preferredVendorName = d.preferred_vendor_name ?? "";
      this.sizeChartUrl = d.size_chart_url ?? "";
      this.selectedSizeChartFile = null;
      this.images.forEach((img) => {
        if (img.localFile) URL.revokeObjectURL(img.image_url);
      });
      this.images = (d.images ?? [])
        .filter((img) => img.active)
        .map((img) => ({
          id: img.id,
          image_url: img.image_url,
          is_default: asFlag(img.is_default),
          preferred: asFlag(img.preferred),
          active: asFlag(img.active),
        }));
      this.zohoId = d.zoho_id ?? "";
      this.hsnCode = d.hsn_code ?? "";
      this.gstPercentage =
        d.gst_percentage != null ? String(d.gst_percentage) : "";
      this.fastProcurement = asFlag(d.fast_procurement);
      this.preferredPrintingTypeId = d.preferred_printing_type_id;
      this.preferredPrintingTypeName =
        d.printing_types?.find(
          (p) => p.printing_type_id === d.preferred_printing_type_id,
        )?.printing_type_name ?? "";
      this.vendorIds = (d.vendors ?? [])
        .filter((v) => v.active)
        .map((v) => v.vendor_id);
      this.printingTypeIds = (d.printing_types ?? [])
        .filter((p) => p.active)
        .map((p) => p.printing_type_id);
      this.extraCategoryIds = (d.categories ?? [])
        .filter((c) => c.active && !c.is_primary)
        .map((c) => c.category_id);
      this.seoTitle = d.seo_title ?? "";
      this.seoDescription = d.seo_description ?? "";
      this.savedVariants = (d.variants ?? [])
        .filter((v) => v.active)
        .map((v) => ({
          id: v.id,
          name: v.name,
          title: v.title,
          hex_code: v.hex_code,
          cost_price: v.cost_price,
          selling_price: v.selling_price,
          option_detail_ids: v.option_detail_ids ?? [],
          active: 1,
        }));
      this.pendingVariants = [];
    },
    removeSavedVariant(variantId: number) {
      this.savedVariants = this.savedVariants.filter((v) => v.id !== variantId);
    },
    setDisplay(value: number) {
      this.display = value;
    },
    setPrintOnDemand(value: number) {
      this.printOnDemand = value;
    },
    setBestSelling(value: number) {
      this.isBestSelling = value;
    },
    setFastProcurement(value: number) {
      this.fastProcurement = value;
    },
  }));

  const debouncedCategorySearch = useDebounce(page.categorySearch, 500);
  const debouncedVendorSearch = useDebounce(page.vendorSearch, 500);
  const debouncedVariantSearch = useDebounce(page.variantSearch, 400);

  useEffect(() => {
    return () => {
      page.images.forEach((img) => {
        if (img.localFile) URL.revokeObjectURL(img.image_url);
      });
    };
  }, [page]);

  useEffect(() => {
    if (isNew || !productId || Number.isNaN(productId)) {
      page.loading = false;
      return;
    }
    let cancelled = false;
    const load = async () => {
      try {
        page.loading = true;
        await catalogProductStore.getProductById(productId);
        if (cancelled) return;
        runInAction(() => page.hydrateFromDetail());
        if (asFlag(catalogProductStore.productDetail?.print_on_demand) === 1) {
          try {
            await catalogProductStore.getPod(productId);
            if (!cancelled) runInAction(() => page.hydratePod());
          } catch {
            if (!cancelled) {
              catalogProductStore.clearProductPod();
              runInAction(() => page.hydratePod());
            }
          }
        } else {
          catalogProductStore.clearProductPod();
          runInAction(() => page.hydratePod());
        }
      } catch (error) {
        if (!cancelled) {
          showError(
            "Error",
            getApiErrorMessage(error, "Failed to load product."),
          );
        }
      } finally {
        if (!cancelled) page.loading = false;
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId, isNew]);

  useEffect(() => {
    if (isNew || !productId || Number.isNaN(productId)) {
      runInAction(() => {
        catalogProductStore.purchaseRequestInsights = [];
      });
      return;
    }

    catalogProductStore.fetchPurchaseRequestInsights(productId).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId, isNew]);

  useEffect(() => {
    if (isNew || !productId || !Number.isFinite(productId) || productId <= 0) {
      return;
    }
    let cancelled = false;
    setPsdLoadingOptions(true);
    Promise.all([
      mockupConfigAPI.getPlacementTypes(),
      mockupConfigAPI.getProductPsdTemplates(productId),
    ])
      .then(([placementData, templates]) => {
        if (cancelled) return;
        podCatalogStore.setDetailPsdPlacementTypes(placementData || []);
        podCatalogStore.setProductPsdTemplates(templates || []);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        showError(
          "Error occurred",
          getApiErrorMessage(err, "Unable to load PSD data."),
        );
      })
      .finally(() => {
        if (!cancelled) setPsdLoadingOptions(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId, isNew]);

  useEffect(() => {
    let cancelled = false;
    categoryStore
      .listCategories({
        page_no: 1,
        limit: 50,
        search: debouncedCategorySearch.trim() || undefined,
        active: true,
        sort_by: "name",
        sort_type: SortType.ASC,
      })
      .then(() => {
        if (cancelled) return;
        page.categoryList = categoryStore.categories.map((c) => ({
          id: String(c.id),
          value: c.name,
        }));
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedCategorySearch]);

  useEffect(() => {
    let cancelled = false;
    vendorAPI
      .getVendors({
        page_no: 1,
        limit: 50,
        search_text: debouncedVendorSearch.trim() || "",
        status: 1,
      })
      .then((res) => {
        if (cancelled) return;
        page.vendorList = (res.list ?? []).map((v) => ({
          id: String(v.id),
          value: v.name || `Vendor #${v.id}`,
        }));
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedVendorSearch]);

  useEffect(() => {
    let cancelled = false;
    productAPI
      .getPrintingTypes()
      .then((res) => {
        if (cancelled) return;
        const list = (res?.result ?? res) as Array<{
          id?: number;
          printing_type_id?: number;
          name?: string;
          printing_type_name?: string;
        }>;
        const rows = Array.isArray(list) ? list : [];
        page.printingTypeList = rows.map((r) => {
          const pid = Number(r.printing_type_id ?? r.id) || 0;
          return {
            id: String(pid),
            value: r.printing_type_name || r.name || `Type #${pid}`,
          };
        });
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const parseNum = (raw: string): number | null => {
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  };

  const getDescriptionPlainText = (html: string): string =>
    html
      .replace(/<[^>]*>/g, " ")
      .replace(/&nbsp;/gi, " ")
      .replace(/\s+/g, " ")
      .trim();

  const isDescriptionProvided = (): boolean =>
    getDescriptionPlainText(page.desc).length > 0;

  const sectionBadge = (label: string) => (
    <Chip
      label={label}
      size="small"
      sx={{
        ml: 1,
        borderRadius: 2,
        backgroundColor: colors.white,
        color: colors.greyTextColor,
        border: `1px solid ${colors.greyBackground}`,
        height: "20px",
        fontSize: "0.65rem",
        "& .MuiChip-label": {
          px: 0.8,
          py: 0,
        },
      }}
    />
  );

  const fieldLabel = (
    label: string,
    options?: {
      required?: boolean;
      iconSrc?: string;
      action?: React.ReactNode;
    },
  ) => (
    <Box display="flex" alignItems="center" gap={1} mb={1}>
      {options?.iconSrc ? (
        <img src={options.iconSrc} alt="" style={{ width: 17, height: 17 }} />
      ) : null}
      <CustomTypography variant="heading8">
        {label}
        {options?.required ? (
          <span style={{ color: theme.palette.red.main }}> *</span>
        ) : null}
      </CustomTypography>
      {options?.action}
    </Box>
  );

  const saveButtonStyles = {
    width: { xs: "100%", sm: 120, md: 150 },
    background: `linear-gradient(90deg, ${colors.purpleGradientStart} 0%, ${colors.purpleGradientEnd} 100%)`,
    color: colors.white,
    "&:hover": {
      background: `linear-gradient(90deg, ${colors.purpleGradientEnd} 0%, ${colors.purpleGradientStart} 100%)`,
    },
  };

  const sectionShell = (
    key: SectionKey,
    title: string,
    badge: string,
    fieldHint: string,
    iconSrc: string,
    children: React.ReactNode,
    actions?: React.ReactNode,
  ) => (
    <Card sx={{ marginTop: 2, marginBottom: 2 }}>
      <CardHeader
        onClick={() => page.toggleSection(key)}
        title={
          <Box display="flex" alignItems="center" gap={1}>
            <img src={iconSrc} alt="" style={{ width: 30, height: 30 }} />
            <CustomTypography variant="heading5">{title}</CustomTypography>
            {sectionBadge(badge)}
          </Box>
        }
        action={
          <Box display="flex" alignItems="center" gap={1}>
            <Box sx={{ display: { xs: "none", sm: "block" } }}>
              <CustomTypography variant="body2" color="text.secondary">
                {fieldHint}
              </CustomTypography>
            </Box>
            <IconButton>
              {page.openSections[key] ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
          </Box>
        }
        sx={{
          cursor: "pointer",
          px: 3,
          py: 2,
          borderBottom: `1px solid ${colors.greyBackground}`,
          "&:hover": {
            backgroundColor: colors.greyBackground,
          },
        }}
      />
      <Collapse in={page.openSections[key]} timeout="auto" unmountOnExit>
        <CardContent sx={{ px: 4 }}>{children}</CardContent>
        {actions ? (
          <CardActions
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: { xs: "stretch", sm: "center" },
              flexDirection: { xs: "column", sm: "row" },
              gap: { xs: 2, sm: 0 },
              px: 4,
              py: 3,
              borderTop: `1px solid ${colors.lightBorder}`,
            }}
          >
            <Box
              display="flex"
              gap={2}
              flexDirection={{ xs: "column", sm: "row" }}
            >
              {actions}
            </Box>
          </CardActions>
        ) : null}
      </Collapse>
    </Card>
  );

  const flagRow = (
    label: string,
    checked: boolean,
    onToggle: (next: boolean) => void,
    locked = false,
  ) => {
    const disabled = locked || page.saving;
    return (
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        onClick={() => {
          if (disabled) return;
          onToggle(!checked);
        }}
        sx={{
          px: 1.5,
          py: 1,
          borderRadius: 1.5,
          border: `1px solid ${colors.greyBackground}`,
          backgroundColor: colors.white,
          cursor: disabled ? "not-allowed" : "pointer",
        }}
      >
        <CustomTypography variant="body2" fontWeight={500}>
          {label}
        </CustomTypography>
        <Box display="flex" alignItems="center" gap={1}>
          <Switch
            checked={checked}
            size="small"
            color="success"
            disabled={disabled}
            onChange={() => {}}
            sx={{ pointerEvents: "none" }}
          />
          <CustomTypography
            variant="body2"
            color={checked ? "success.main" : "text.secondary"}
            fontWeight={500}
          >
            {checked ? "ON" : "OFF"}
          </CustomTypography>
        </Box>
      </Box>
    );
  };

  const handleCreate = async () => {
    if (!canEdit) {
      showError("Permission denied", "You cannot create products.");
      return;
    }
    if (!page.name.trim()) {
      showError("Validation", "Product name is required.");
      return;
    }
    if (!isDescriptionProvided()) {
      showError("Validation", "Product description is required.");
      return;
    }
    if (!page.categoryId) {
      showError("Validation", "Primary category is required.");
      return;
    }
    const cost = parseNum(page.costPrice);
    const sell = parseNum(page.sellingPrice);
    if (cost == null || cost < 0) {
      showError("Validation", "Valid cost price is required.");
      return;
    }
    if (sell == null || sell < 0) {
      showError("Validation", "Valid selling price is required.");
      return;
    }

    const body: CreateProductInput = {
      name: page.name.trim(),
      category_id: page.categoryId,
      cost_price: cost,
      selling_price: sell,
      desc: page.desc.trim() || null,
      display: page.display,
      category_ids: page.extraCategoryIds,
    };

    try {
      page.saving = true;
      const created = await catalogProductStore.createProduct(body);
      if (page.pendingVariants.length > 0) {
        try {
          await catalogProductStore.updateVariants(created.id, {
            variants: page.pendingVariants
              .filter((v) => (v.option_detail_ids?.length ?? 0) > 0)
              .map((v) => ({
                name: v.name,
                title: v.title ?? null,
                hex_code: v.hex_code ?? null,
                option_detail_ids: v.option_detail_ids,
                cost_price: v.cost_price ?? null,
                selling_price: v.selling_price ?? null,
                active: 1,
              })),
          });
        } catch (error) {
          showError(
            "Warning",
            getApiErrorMessage(
              error,
              "Product created but variants could not be saved.",
            ),
          );
        }
      }
      navigate(`/products/${created.id}`, { replace: true });
    } catch (error) {
      showError(
        "Error",
        getApiErrorMessage(error, "Could not create product."),
      );
    } finally {
      page.saving = false;
    }
  };

  const handleSaveCore = async () => {
    if (!canEdit || !productId) return;
    if (!page.name.trim()) {
      showError("Validation", "Product name is required.");
      return;
    }
    if (!isDescriptionProvided()) {
      showError("Validation", "Product description is required.");
      return;
    }
    try {
      page.saving = true;
      await catalogProductStore.updateProduct(productId, {
        name: page.name.trim(),
        category_id: page.categoryId || undefined,
        cost_price: parseNum(page.costPrice) ?? undefined,
        selling_price: parseNum(page.sellingPrice) ?? undefined,
        desc: page.desc.trim(),
        display: page.display,
        brand_name: page.brandName.trim() || null,
        dimensions: page.dimensions.trim() || null,
        sizes: page.sizes.trim() || null,
        capacity: page.capacity.trim() || null,
        colors: page.colors.trim() || null,
        priority: parseNum(page.priority) ?? 0,
        length: parseNum(page.length),
        breadth: parseNum(page.breadth),
        height: parseNum(page.height),
        weight: parseNum(page.weight),
        print_on_demand: page.printOnDemand,
        is_best_selling: page.isBestSelling,
      });
      page.hydrateFromDetail();
      if (page.printOnDemand === 1) {
        try {
          await catalogProductStore.getPod(productId);
          page.hydratePod();
        } catch {
          catalogProductStore.clearProductPod();
          page.hydratePod();
        }
      } else {
        catalogProductStore.clearProductPod();
        page.hydratePod();
      }
    } catch (error) {
      showError(
        "Error",
        getApiErrorMessage(error, "Could not save product details."),
      );
    } finally {
      page.saving = false;
    }
  };

  const isPodEnabled = page.printOnDemand === 1;

  const isVariantInPod = (variantId: number | undefined) => {
    if (!variantId) return false;
    return (catalogProductStore.productPod?.variants ?? []).some(
      (pv) => pv.variant_id === variantId && pv.active,
    );
  };

  const handleSavePod = async () => {
    if (!canEdit || !productId) return;
    if (!isPodEnabled) {
      showError(
        "Print on demand off",
        "Enable Print on Demand on the product before editing POD details.",
      );
      return;
    }
    const selling = parseNum(page.podSellingPrice);
    const production = parseNum(page.podProductionTime);
    if (selling == null && production == null) {
      showError(
        "Validation",
        "Enter selling price and/or production time (days).",
      );
      return;
    }
    if (selling != null && selling < 0) {
      showError("Validation", "POD selling price must be 0 or greater.");
      return;
    }
    if (
      production != null &&
      (!Number.isInteger(production) || production <= 0)
    ) {
      showError(
        "Validation",
        "Production time must be a positive whole number of days.",
      );
      return;
    }
    try {
      page.saving = true;
      await catalogProductStore.updatePod(productId, {
        ...(selling != null ? { pod_selling_price: selling } : {}),
        ...(page.podProductionTime.trim() === ""
          ? { production_time: null }
          : production != null
            ? { production_time: production }
            : {}),
      });
      page.hydratePod();
    } catch (error) {
      showError(
        "Error",
        getApiErrorMessage(error, "Could not save POD details."),
      );
    } finally {
      page.saving = false;
    }
  };

  const handleTogglePodVariant = async (variantId: number, next: boolean) => {
    if (!canEdit || !productId) return;
    if (!isPodEnabled) {
      showError(
        "Print on demand off",
        "Enable Print on Demand on the product before mapping variants.",
      );
      return;
    }
    const existing = catalogProductStore.productPod?.variants?.find(
      (pv) => pv.variant_id === variantId,
    );
    try {
      page.saving = true;
      await catalogProductStore.updatePodVariants(productId, {
        variants: [
          {
            ...(existing?.id ? { id: existing.id } : {}),
            variant_id: variantId,
            active: next ? 1 : 0,
          },
        ],
      });
    } catch (error) {
      showError(
        "Error",
        getApiErrorMessage(error, "Could not update POD variants."),
      );
    } finally {
      page.saving = false;
    }
  };

  const variantKey = (v: {
    option_detail_ids?: number[];
    name: string;
    title?: string | null;
  }) => {
    const idsKey = variantOptionDetailsKey(v.option_detail_ids);
    if (idsKey) return idsKey;
    return (v.title || v.name || "").trim().toLowerCase();
  };

  const toVariantPayload = (
    v: UpdateVariantsInput["variants"][number],
    active: number,
  ): UpdateVariantsInput["variants"][number] => ({
    ...(v.id ? { id: v.id } : {}),
    name: v.name,
    title: v.title ?? null,
    hex_code: v.hex_code ?? null,
    option_detail_ids: v.option_detail_ids,
    cost_price: v.cost_price ?? null,
    selling_price: v.selling_price ?? null,
    active,
  });

  const handleSaveVariants = async () => {
    if (!canEdit || !productId) return;

    const existingKeys = new Set(page.savedVariants.map(variantKey));
    const newOnes = page.pendingVariants.filter(
      (v) =>
        (v.option_detail_ids?.length ?? 0) > 0 &&
        !existingKeys.has(variantKey(v)),
    );

    const removed =
      catalogProductStore.productDetail?.variants
        ?.filter(
          (v) =>
            Boolean(v.id) &&
            v.active &&
            !page.savedVariants.some((s) => s.id === v.id),
        )
        .map((v) =>
          toVariantPayload(
            {
              id: v.id,
              name: v.name,
              title: v.title,
              hex_code: v.hex_code,
              cost_price: v.cost_price,
              selling_price: v.selling_price,
              option_detail_ids: v.option_detail_ids ?? [],
            },
            0,
          ),
        )
        .filter((v) => v.option_detail_ids.length > 0) ?? [];

    const savedPayload = page.savedVariants
      .filter((v) => (v.option_detail_ids?.length ?? 0) > 0)
      .map((v) => toVariantPayload(v, 1));

    if (
      savedPayload.length === 0 &&
      newOnes.length === 0 &&
      removed.length === 0
    ) {
      showError(
        "Validation",
        "Select options and values to add at least one variant.",
      );
      return;
    }

    if (
      page.pendingVariants.length > 0 &&
      newOnes.length === 0 &&
      removed.length === 0
    ) {
      showError(
        "Validation",
        "Those variants already exist. Select different values to add new ones.",
      );
      return;
    }

    const missingDetails = page.savedVariants.some(
      (v) => !(v.option_detail_ids?.length > 0),
    );

    if (missingDetails) {
      showError(
        "Validation",
        "Some saved variants are missing option details. Remove them and re-add from the picker.",
      );
      return;
    }

    try {
      page.saving = true;
      await catalogProductStore.updateVariants(productId, {
        variants: [
          ...savedPayload,
          ...newOnes.map((v) => toVariantPayload(v, 1)),
          ...removed,
        ],
      });
      page.hydrateFromDetail();
      page.variantsBuilderResetKey += 1;
    } catch (error) {
      showError("Error", getApiErrorMessage(error, "Could not save variants."));
    } finally {
      page.saving = false;
    }
  };

  const handleSaveMedia = async () => {
    if (!canEdit || !productId) return;
    try {
      page.saving = true;
      const pending = page.images.filter((img) => img.localFile);
      let uploadedUrls: string[] = [];
      if (pending.length > 0) {
        const imageAPI = new ImageAPI();
        uploadedUrls = await imageAPI.uploadImage(
          pending.map((img) => img.localFile as File),
          "product-images",
        );
      }

      let sizeChartUrl = page.sizeChartUrl.trim() || null;
      if (page.selectedSizeChartFile) {
        const imageAPI = new ImageAPI();
        const sizeChartUrls = await imageAPI.uploadImage(
          [page.selectedSizeChartFile],
          "product-images",
        );
        if (!sizeChartUrls.length || !sizeChartUrls[0]) {
          showError("Error", "Size chart failed to upload. Please try again.");
          return;
        }
        sizeChartUrl = sizeChartUrls[0];
        page.sizeChartUrl = sizeChartUrls[0];
      }

      let uploadIdx = 0;
      const keptPayload: UpdateImagesInput["images"] = page.images.map(
        (img) => {
          if (img.localFile) {
            const url = uploadedUrls[uploadIdx++] || "";
            URL.revokeObjectURL(img.image_url);
            return {
              image_url: url,
              is_default: img.is_default,
              preferred: img.preferred ?? 0,
              active: 1,
            };
          }
          return {
            id: img.id,
            image_url: img.image_url,
            is_default: img.is_default,
            preferred: img.preferred ?? 0,
            active: 1,
          };
        },
      );

      if (keptPayload.some((img) => !img.image_url)) {
        showError("Error", "Some images failed to upload. Please try again.");
        return;
      }

      // Backend soft-deletes via active: 0; omitted ids are left unchanged.
      const keptIds = new Set(
        keptPayload
          .map((img) => img.id)
          .filter((id): id is number => typeof id === "number" && id > 0),
      );
      const removedPayload: UpdateImagesInput["images"] = (
        catalogProductStore.productDetail?.images ?? []
      )
        .filter((img) => img.active && img.id > 0 && !keptIds.has(img.id))
        .map((img) => ({
          id: img.id,
          image_url: img.image_url,
          is_default: 0,
          preferred: 0,
          active: 0,
        }));

      await catalogProductStore.updateImages(productId, {
        size_chart_url: sizeChartUrl,
        images: [...keptPayload, ...removedPayload],
      });
      page.clearSelectedSizeChartFile();
      page.hydrateFromDetail();
    } catch (error) {
      showError("Error", getApiErrorMessage(error, "Could not save images."));
    } finally {
      page.saving = false;
    }
  };

  const handleUploadPodPsd = async () => {
    if (!productId || !Number.isFinite(productId) || productId <= 0) return;

    const parsedPlacement = Number(podCatalogStore.detailPsdPlacementTypeId);
    if (!Number.isFinite(parsedPlacement) || parsedPlacement <= 0) {
      showError("Error occurred", "Select a placement type before uploading.");
      return;
    }
    const variantSku = podCatalogStore.detailPsdVariantSku.trim();
    if (!variantSku) {
      showError("Error occurred", "Select a SKU before uploading.");
      return;
    }
    setPsdUploading(true);
    try {
      const res = await podCatalogStore.uploadProductPsd({
        file: podCatalogStore.detailPsdFile,
        product_id: productId,
        placetype_id: parsedPlacement,
        variant_sku: variantSku,
      });
      podCatalogStore.setDetailPsdFile(null);
      await podCatalogStore.loadProductPsdTemplates(productId);
    } catch (err: unknown) {
      showError(
        "Error occurred",
        getApiErrorMessage(err, "Unable to upload PSD."),
      );
    } finally {
      setPsdUploading(false);
    }
  };

  const handleSetDefaultPsdTemplate = async (templateId: number) => {
    if (!productId) return;
    try {
      const res = await podCatalogStore.setDefaultProductPsdTemplate(
        productId,
        templateId,
      );
    } catch (err: unknown) {
      showError(
        "Error occurred",
        getApiErrorMessage(err, "Unable to set default PSD template."),
      );
    }
  };

  const handleRemovePsdTemplate = async (templateId: number) => {
    if (!productId) return;
    try {
      const res = await podCatalogStore.removeProductPsdTemplate(
        productId,
        templateId,
      );
    } catch (err: unknown) {
      showError(
        "Error occurred",
        getApiErrorMessage(err, "Unable to remove PSD template."),
      );
    }
  };

  const handleDownloadPsdTemplate = (templateId: number) => {
    if (!productId) return;
    const url = podCatalogStore.getProductPsdTemplateDownloadUrl(
      productId,
      templateId,
    );
    window.open(url, "_blank");
  };

  const handleSaveSourcing = async () => {
    if (!canEditSourcing || !productId) return;
    try {
      page.saving = true;
      await catalogProductStore.updateSourcing(productId, {
        zoho_id: page.zohoId.trim() || null,
        hsn_code: page.hsnCode.trim() || null,
        gst_percentage: parseNum(page.gstPercentage),
        fast_procurement: page.fastProcurement,
        preferred_printing_type_id: page.preferredPrintingTypeId,
        preferred_vendor_id: page.preferredVendorId,
      });
      await catalogProductStore.updateVendors(productId, {
        preferred_vendor_id: page.preferredVendorId,
        vendors: page.vendorIds.map((vendor_id) => ({
          vendor_id,
          active: 1,
        })),
      });
      if (canEditPrinting) {
        await catalogProductStore.updatePrintingTypes(productId, {
          preferred_printing_type_id: page.preferredPrintingTypeId,
          printing_types: page.printingTypeIds.map((printing_type_id) => ({
            printing_type_id,
            active: 1,
          })),
        });
      }
      page.hydrateFromDetail();
    } catch (error) {
      showError("Error", getApiErrorMessage(error, "Could not save sourcing."));
    } finally {
      page.saving = false;
    }
  };

  const handleSaveCategories = async () => {
    if (!canEdit || !productId || !page.categoryId) return;
    try {
      page.saving = true;
      await catalogProductStore.updateCategories(productId, {
        category_id: page.categoryId,
        categories: page.extraCategoryIds.map((category_id) => ({
          category_id,
          active: 1,
        })),
      });
      page.hydrateFromDetail();
    } catch (error) {
      showError(
        "Error",
        getApiErrorMessage(error, "Could not save categories."),
      );
    } finally {
      page.saving = false;
    }
  };

  const handleSaveSeo = async () => {
    if (!canEditSeo || !productId) return;
    if (!page.seoTitle.trim() || !page.seoDescription.trim()) {
      showError("Validation", "SEO title and description are required.");
      return;
    }
    try {
      page.saving = true;
      await catalogProductStore.updateSeo(productId, {
        seo_title: page.seoTitle.trim(),
        seo_description: page.seoDescription.trim(),
      });
      page.hydrateFromDetail();
    } catch (error) {
      showError("Error", getApiErrorMessage(error, "Could not save SEO."));
    } finally {
      page.saving = false;
    }
  };

  const handleUploadImages = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files?.length || !canEdit) return;
    const selected = Array.from(files);
    const next: ProductMediaImage[] = selected.map((file, index) => ({
      image_url: URL.createObjectURL(file),
      localFile: file,
      is_default: page.images.length === 0 && index === 0 ? 1 : 0,
      preferred: 0,
      active: 1,
    }));
    page.images = [...page.images, ...next];
    event.target.value = "";
  };

  const handleToggleActive = async () => {
    if (!canEdit || !productId) return;
    try {
      page.saving = true;
      await catalogProductStore.toggleActive(productId);
      page.deleteOpen = false;
    } catch (error) {
      showError(
        "Error",
        getApiErrorMessage(error, "Could not update active status."),
      );
    } finally {
      page.saving = false;
    }
  };

  if (page.loading) {
    return (
      <Box
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  const detail = catalogProductStore.productDetail;

  const filteredSavedVariants = page.savedVariants.filter((v) => {
    const term = debouncedVariantSearch.trim().toLowerCase();
    if (!term) return true;
    const title = (v.title || "").toLowerCase();
    const name = (v.name || "").toLowerCase();
    return title.includes(term) || name.includes(term);
  });

  return (
    <Box>
      <Box
        sx={{
          backgroundColor: colors.white,
          borderRadius: 1,
          marginLeft: 2,
          marginRight: 2,
        }}
      >
        <Box
          sx={{
            paddingLeft: 2,
            paddingTop: 2,
            display: "flex",
            alignItems: "center",
            gap: "0.5rem",
            cursor: "pointer",
            color: colors.greyTextColor,
            maxWidth: "3.75rem",
            "&:hover, &:active": {
              color: colors.greyTextColor,
            },
          }}
          onClick={() => {
            if (window.history.state?.idx > 0) {
              navigate(-1);
            } else {
              navigate("/products/listing");
            }
          }}
        >
          <ArrowBackIcon sx={{ fontSize: "1rem" }} />
          <span style={{ fontSize: "0.875rem", fontWeight: "600" }}>Back</span>
        </Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            backgroundColor: colors.white,
            p: 2,
            borderRadius: 1,
          }}
        >
          <Box>
            <CustomTypography variant="heading4">
              {isNew ? "Create Product" : "Update Product"}
            </CustomTypography>
            <CustomTypography
              variant="body2"
              color={colors.greyTextColor}
              style={{ marginTop: 5 }}
            >
              Create and manage your product information with ease.
            </CustomTypography>
            {!isNew && detail ? (
              <Box display="flex" gap={1} mt={1} flexWrap="wrap">
                <Chip
                  size="small"
                  label={detail.active ? "Active" : "Inactive"}
                  sx={{
                    borderRadius: 2,
                    backgroundColor: colors.white,
                    color: colors.greyTextColor,
                    border: `1px solid ${colors.greyBackground}`,
                    height: 22,
                    fontSize: "0.65rem",
                  }}
                />
                <Chip
                  size="small"
                  label={detail.display ? "Displayed" : "Hidden"}
                  sx={{
                    borderRadius: 2,
                    backgroundColor: colors.white,
                    color: colors.greyTextColor,
                    border: `1px solid ${colors.greyBackground}`,
                    height: 22,
                    fontSize: "0.65rem",
                  }}
                />
                {detail.urgent_sourcing_details && (
                  <Chip
                    size="small"
                    label={`Urgent Sourcing ID #${detail.urgent_sourcing_details.id}`}
                    sx={{
                      borderRadius: 2,
                      backgroundColor: colors.white,
                      color: colors.greyTextColor,
                      border: `1px solid ${colors.greyBackground}`,
                      height: 22,
                      fontSize: "0.65rem",
                    }}
                  />
                )}
                {detail.sku ? (
                  <Chip
                    size="small"
                    label={`SKU ${detail.sku}`}
                    sx={{
                      borderRadius: 2,
                      backgroundColor: colors.white,
                      color: colors.greyTextColor,
                      border: `1px solid ${colors.greyBackground}`,
                      height: 22,
                      fontSize: "0.65rem",
                    }}
                  />
                ) : null}
              </Box>
            ) : null}
            {!isNew && detail ? (
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: { xs: 1, sm: 3 },
                  mt: 1.5,
                }}
              >
                <Box>
                  <CustomTypography
                    variant="body3"
                    color={colors.greyTextColor}
                    sx={{ textTransform: "uppercase" }}
                  >
                    Created by
                  </CustomTypography>
                  <CustomTypography variant="body2" fontWeight={600}>
                    {detail.created_by_name?.trim() || "-"}
                  </CustomTypography>
                  <CustomTypography
                    variant="body3"
                    color={colors.greyTextColor}
                  >
                    {detail.created_date
                      ? formatDate(detail.created_date)
                      : "-"}
                  </CustomTypography>
                </Box>
                <Box>
                  <CustomTypography
                    variant="body3"
                    color={colors.greyTextColor}
                    sx={{ textTransform: "uppercase" }}
                  >
                    Last modified by
                  </CustomTypography>
                  <CustomTypography variant="body2" fontWeight={600}>
                    {detail.last_modified_by_name?.trim() || "-"}
                  </CustomTypography>
                  <CustomTypography
                    variant="body3"
                    color={colors.greyTextColor}
                  >
                    {detail.modified_date
                      ? formatDate(detail.modified_date)
                      : "-"}
                  </CustomTypography>
                </Box>
              </Box>
            ) : null}
          </Box>
          <Box
            display="flex"
            gap={1.5}
            flexWrap="wrap"
            sx={{ width: { xs: "100%", sm: "auto" } }}
          >
            {!isNew && productId ? (
              <AppButton
                variant="secondary"
                label="Activity Logs"
                startIcon={<HistoryIcon />}
                onClick={() => setActivityLogsDrawerOpen(true)}
                customStyles={{ width: { xs: "100%", sm: 160 } }}
              />
            ) : null}
            {!isNew && canEdit ? (
              <AppButton
                variant="grayscale"
                label={detail?.active ? "Deactivate" : "Restore"}
                onClick={() => {
                  page.deleteOpen = true;
                }}
                customStyles={{ width: { xs: "100%", sm: 140 } }}
              />
            ) : null}
          </Box>
        </Box>
      </Box>

      <Box px={2} pb={4}>
        {sectionShell(
          "core",
          "Product Information",
          "Core Details",
          "Key fields",
          ProductIcon,
          <Box>
            <GradientDivider title="Basics" color="128,0,255" sx={{ my: 4 }} />
            <Grid2 container spacing={2} columns={12}>
              <Grid2 size={{ xs: 12, md: 6 }}>
                {fieldLabel("Name", { required: true, iconSrc: NotesIcon })}
                <CustomTextField
                  width="100%"
                  value={page.name}
                  disabled={!canEdit || page.saving}
                  borderColor={colors.greyBackground}
                  onChange={(e) => {
                    page.name = e.target.value;
                  }}
                />
              </Grid2>
              <Grid2 size={{ xs: 6, md: 3 }}>
                {fieldLabel("Primary category", {
                  required: true,
                  iconSrc: StatusIcon,
                })}
                <AppSearchDropdownMulti
                  title="Select category"
                  isMultiSelect={false}
                  local
                  disabled={!canEdit || page.saving}
                  list={page.categoryList}
                  value={
                    page.categoryId
                      ? [
                          {
                            id: String(page.categoryId),
                            value:
                              page.categoryName ||
                              `Category #${page.categoryId}`,
                          },
                        ]
                      : []
                  }
                  handleSearch={(term) => {
                    page.categorySearch = term || "";
                  }}
                  handleSelect={async (items) => {
                    const next = items[0];
                    if (!next) {
                      page.categoryId = null;
                      page.categoryName = "";
                      return;
                    }
                    const cid = parseInt(String(next.id), 10);
                    page.categoryId = cid;
                    page.categoryName = next.value;
                  }}
                />
              </Grid2>
              <Grid2 size={{ xs: 12 }}>
                {fieldLabel("Description", {
                  required: true,
                  iconSrc: NotesIcon,
                  action: (
                    <RichTextCopyButton
                      html={page.desc}
                      copyLabel="Copy description"
                    />
                  ),
                })}
                <Box
                  sx={{
                    width: "100%",
                    maxWidth: "100%",
                    overflow: "hidden",
                    "& .ql-container": {
                      maxWidth: "100%",
                      width: "100%",
                      boxSizing: "border-box",
                    },
                    "& .ql-toolbar": {
                      maxWidth: "100%",
                      width: "100%",
                      boxSizing: "border-box",
                    },
                    "& .ql-editor p, & .ql-editor div, & .ql-editor span": {
                      maxWidth: "100%",
                      wordBreak: "break-word",
                      overflowWrap: "break-word",
                    },
                  }}
                >
                  <Box sx={{ width: "100%", maxWidth: "100%" }}>
                    <RichTextEditor
                      value={page.desc || ""}
                      onChange={(value: string) => {
                        page.desc = String(value);
                      }}
                      maxLength={1000}
                      placeholder="Please write description here..."
                      disabled={!canEdit || page.saving}
                      minHeight="100px"
                    />
                  </Box>
                </Box>
              </Grid2>
            </Grid2>

            <GradientDivider
              title="Pricing"
              color="0, 255, 170"
              sx={{ my: 5 }}
            />
            <Grid2 container spacing={2} columns={12}>
              <Grid2 size={{ xs: 12, md: 3 }}>
                {fieldLabel("Cost price", {
                  required: true,
                  iconSrc: RupeeIcon,
                })}
                <CustomTextField
                  width="100%"
                  value={page.costPrice}
                  disabled={!canEdit || page.saving}
                  borderColor={colors.greyBackground}
                  onChange={(e) => {
                    page.costPrice = e.target.value;
                  }}
                />
              </Grid2>
              <Grid2 size={{ xs: 12, md: 3 }}>
                {fieldLabel("Selling price", {
                  required: true,
                  iconSrc: RupeeIcon,
                })}
                <CustomTextField
                  width="100%"
                  value={page.sellingPrice}
                  disabled={!canEdit || page.saving}
                  borderColor={colors.greyBackground}
                  onChange={(e) => {
                    page.sellingPrice = e.target.value;
                  }}
                />
              </Grid2>
            </Grid2>

            {!isNew ? (
              <>
                <GradientDivider
                  title="Attributes"
                  color="173, 216, 255"
                  sx={{ my: 5 }}
                />
                <Grid2 container spacing={2} columns={12}>
                  <Grid2 size={{ xs: 12, md: 4 }}>
                    {fieldLabel("Brand")}
                    <CustomTextField
                      width="100%"
                      value={page.brandName}
                      disabled={!canEdit || page.saving}
                      borderColor={colors.greyBackground}
                      onChange={(e) => {
                        page.brandName = e.target.value;
                      }}
                    />
                  </Grid2>
                  <Grid2 size={{ xs: 12, md: 4 }}>
                    {fieldLabel("Priority")}
                    <CustomTextField
                      width="100%"
                      value={page.priority}
                      disabled={!canEdit || page.saving}
                      borderColor={colors.greyBackground}
                      onChange={(e) => {
                        page.priority = e.target.value;
                      }}
                    />
                  </Grid2>
                  <Grid2 size={{ xs: 6, md: 3 }}>
                    {fieldLabel("Length")}
                    <CustomTextField
                      width="100%"
                      value={page.length}
                      disabled={!canEdit || page.saving}
                      borderColor={colors.greyBackground}
                      onChange={(e) => {
                        page.length = e.target.value;
                      }}
                    />
                  </Grid2>
                  <Grid2 size={{ xs: 6, md: 3 }}>
                    {fieldLabel("Breadth")}
                    <CustomTextField
                      width="100%"
                      value={page.breadth}
                      disabled={!canEdit || page.saving}
                      borderColor={colors.greyBackground}
                      onChange={(e) => {
                        page.breadth = e.target.value;
                      }}
                    />
                  </Grid2>
                  <Grid2 size={{ xs: 6, md: 3 }}>
                    {fieldLabel("Height")}
                    <CustomTextField
                      width="100%"
                      value={page.height}
                      disabled={!canEdit || page.saving}
                      borderColor={colors.greyBackground}
                      onChange={(e) => {
                        page.height = e.target.value;
                      }}
                    />
                  </Grid2>
                  <Grid2 size={{ xs: 6, md: 3 }}>
                    {fieldLabel("Weight")}
                    <CustomTextField
                      width="100%"
                      value={page.weight}
                      disabled={!canEdit || page.saving}
                      borderColor={colors.greyBackground}
                      onChange={(e) => {
                        page.weight = e.target.value;
                      }}
                    />
                  </Grid2>
                </Grid2>
                <GradientDivider title="Settings" sx={{ my: 5 }} />
                <Grid2 container spacing={2} columns={12}>
                  <Grid2 size={{ xs: 12, md: 4 }}>
                    {flagRow(
                      "Display on storefront",
                      page.display === 1,
                      (n) => page.setDisplay(n ? 1 : 0),
                      !canEdit,
                    )}
                  </Grid2>
                  <Grid2 size={{ xs: 12, md: 4 }}>
                    {flagRow(
                      "Print on demand",
                      page.printOnDemand === 1,
                      (n) => page.setPrintOnDemand(n ? 1 : 0),
                      !canEdit,
                    )}
                  </Grid2>
                  <Grid2 size={{ xs: 12, md: 4 }}>
                    {flagRow(
                      "Best selling",
                      page.isBestSelling === 1,
                      (n) => page.setBestSelling(n ? 1 : 0),
                      !canEdit,
                    )}
                  </Grid2>
                </Grid2>
              </>
            ) : (
              <>
                <GradientDivider title="Settings" sx={{ my: 3 }} />
                <Grid2 container spacing={2} columns={12}>
                  <Grid2 size={{ xs: 12, md: 4 }}>
                    {flagRow(
                      "Display on storefront",
                      page.display === 1,
                      (n) => page.setDisplay(n ? 1 : 0),
                      !canEdit,
                    )}
                  </Grid2>
                </Grid2>
              </>
            )}
          </Box>,
          canEdit ? (
            <AppButton
              variant="primary"
              label={isNew ? "Create Product" : "Save Details"}
              onClick={() => {
                void (isNew ? handleCreate() : handleSaveCore());
              }}
              disabled={page.saving}
              customStyles={saveButtonStyles}
            />
          ) : null,
        )}

        {sectionShell(
          "sku",
          "Variants",
          "Options & Values",
          "Click to select",
          SkuIcon,
          <Box>
            <GradientDivider
              title="Current variants"
              color="128,0,255"
              sx={{ my: 1 }}
            />
            {page.savedVariants.length === 0 ? (
              <CustomTypography
                variant="body2"
                color={colors.greyTextColor}
                sx={{ mb: 2 }}
              >
                No variants saved yet. Use the picker below to add some.
              </CustomTypography>
            ) : (
              <Box>
                <Grid2 container spacing={2} columns={12} mb={2}>
                  <Grid2 size={{ xs: 12 }}>
                    <CustomTextField
                      width="100%"
                      placeholder="Search variants by title or name…"
                      value={page.variantSearch}
                      disabled={page.saving}
                      borderColor={colors.greyBackground}
                      startAdornment={
                        <Box
                          component="img"
                          src={SearchIcon}
                          alt=""
                          sx={{ width: 14, height: 14, opacity: 0.7 }}
                        />
                      }
                      onChange={(e) => {
                        page.variantSearch = e.target.value || "";
                      }}
                    />
                  </Grid2>
                </Grid2>

                {filteredSavedVariants.length === 0 ? (
                  <CustomTypography
                    variant="body2"
                    color={colors.greyTextColor}
                    sx={{ mb: 2 }}
                  >
                    {debouncedVariantSearch.trim()
                      ? "No variants match your search."
                      : "No variants saved yet. Use the picker below to add some."}
                  </CustomTypography>
                ) : (
                  <Grid2 container spacing={2} columns={12}>
                    {filteredSavedVariants.map((v) => {
                      const serverSku =
                        catalogProductStore.productDetail?.variants?.find(
                          (sv) => sv.id === v.id,
                        )?.sku ?? null;
                      return (
                        <Grid2 key={v.id} size={{ xs: 12, md: 6 }}>
                          <Paper
                            key={
                              v.id ??
                              variantOptionDetailsKey(v.option_detail_ids) ??
                              v.name
                            }
                            variant="outlined"
                            sx={{
                              px: 1.5,
                              py: 1,
                              borderRadius: 1.5,
                              borderColor: colors.greyBackground,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              gap: 1,
                              backgroundColor: colors.white,
                            }}
                          >
                            <Box
                              display="flex"
                              alignItems="center"
                              gap={1}
                              minWidth={0}
                            >
                              {v.hex_code ? (
                                <Box
                                  sx={{
                                    width: 14,
                                    height: 14,
                                    borderRadius: "50%",
                                    bgcolor: v.hex_code,
                                    border: `1px solid ${colors.greyBorder}`,
                                    flexShrink: 0,
                                  }}
                                />
                              ) : null}
                              <Box minWidth={0}>
                                <CustomTypography
                                  variant="body2"
                                  fontWeight={600}
                                >
                                  {v.title || v.name}
                                </CustomTypography>
                                <Box
                                  display="flex"
                                  alignItems="center"
                                  gap={0.75}
                                  flexWrap="wrap"
                                  minWidth={0}
                                >
                                  {v.id ? (
                                    <CustomTypography
                                      variant="caption"
                                      color={colors.greyTextColor}
                                    >
                                      #{v.id}
                                      {" |"}
                                    </CustomTypography>
                                  ) : null}
                                  <SkuCopyRow
                                    sku={serverSku}
                                    emptyFallback="SKU pending"
                                    variant="body3"
                                  />
                                </Box>
                              </Box>
                            </Box>
                            {canEdit ? (
                              <Box
                                display="flex"
                                alignItems="center"
                                gap={1}
                                flexShrink={0}
                              >
                                {v.id && !isNew ? (
                                  <Box
                                    display="flex"
                                    alignItems="center"
                                    gap={0.5}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if (
                                        page.saving ||
                                        !isPodEnabled ||
                                        !v.id
                                      ) {
                                        return;
                                      }
                                      void handleTogglePodVariant(
                                        v.id,
                                        !isVariantInPod(v.id),
                                      );
                                    }}
                                    sx={{
                                      px: 1,
                                      py: 0.25,
                                      borderRadius: 1,
                                      border: `1px solid ${colors.greyBackground}`,
                                      opacity: isPodEnabled ? 1 : 0.5,
                                      cursor:
                                        page.saving || !isPodEnabled
                                          ? "not-allowed"
                                          : "pointer",
                                    }}
                                    title={
                                      isPodEnabled
                                        ? "Add to Print on Demand"
                                        : "Enable Print on Demand to map variants"
                                    }
                                  >
                                    <CustomTypography
                                      variant="caption"
                                      color={colors.greyTextColor}
                                    >
                                      POD
                                    </CustomTypography>
                                    <Switch
                                      size="small"
                                      color="success"
                                      checked={isVariantInPod(v.id)}
                                      disabled={
                                        page.saving || !isPodEnabled || !v.id
                                      }
                                      onChange={() => {}}
                                      sx={{ pointerEvents: "none" }}
                                    />
                                  </Box>
                                ) : null}
                                {canDeleteItemSku ? (
                                  <IconButton
                                    size="small"
                                    disabled={page.saving}
                                    onClick={() => {
                                      if (v.id) page.removeSavedVariant(v.id);
                                    }}
                                    sx={{
                                      color: colors.errorRed,
                                      border: `1px solid ${colors.greyBackground}`,
                                      borderRadius: 1.5,
                                    }}
                                    aria-label={`Remove ${v.title || v.name}`}
                                  >
                                    <DeleteOutlineIcon fontSize="small" />
                                  </IconButton>
                                ) : null}
                              </Box>
                            ) : null}
                          </Paper>
                        </Grid2>
                      );
                    })}
                    <CustomTypography
                      variant="caption"
                      color={colors.greyTextColor}
                    >
                      {filteredSavedVariants.length}
                      {debouncedVariantSearch.trim()
                        ? ` matching of ${page.savedVariants.length}`
                        : ""}{" "}
                      saved variant
                      {filteredSavedVariants.length === 1 ? "" : "s"}
                      {page.pendingVariants.length > 0
                        ? ` · ${page.pendingVariants.length} new selected (not saved yet)`
                        : ""}
                    </CustomTypography>
                  </Grid2>
                )}
              </Box>
            )}

            <GradientDivider
              title="Add variants"
              color="0, 255, 170"
              sx={{ my: 2 }}
            />
            <CustomTypography
              variant="body2"
              color={colors.greyTextColor}
              sx={{ mb: 2 }}
            >
              Click options and values to build new variants. Existing ones stay
              in the list above — save to append the new selections.
            </CustomTypography>
            <ProductSkuBuilder
              key={page.variantsBuilderResetKey}
              categoryId={page.categoryId}
              disabled={!canEdit || page.saving}
              serverSku={page.sku || null}
              resetKey={page.variantsBuilderResetKey}
              onVariantsChange={(variants) => {
                page.pendingVariants = variants;
              }}
            />
            {isNew ? (
              <CustomTypography
                variant="caption"
                color={colors.greyTextColor}
                sx={{ mt: 2, display: "block" }}
              >
                New variants are saved automatically after you create the
                product.
              </CustomTypography>
            ) : null}
          </Box>,
          !isNew && canEdit ? (
            <AppButton
              variant="primary"
              label={
                page.pendingVariants.length > 0
                  ? `Save & add ${page.pendingVariants.length}`
                  : "Save Variants"
              }
              onClick={() => {
                void handleSaveVariants();
              }}
              disabled={page.saving}
              customStyles={saveButtonStyles}
            />
          ) : null,
        )}

        {!isNew
          ? sectionShell(
              "pod",
              "Print On Demand",
              "POD settings",
              isPodEnabled ? "Editable" : "Enable POD flag",
              StatusIcon,
              <Box>
                {!isPodEnabled ? (
                  <CustomTypography
                    variant="body2"
                    color={colors.greyTextColor}
                    sx={{ mb: 2 }}
                  >
                    Enable Print on Demand in Product Details and save to edit
                    POD pricing, production time, and published variants.
                  </CustomTypography>
                ) : null}
                <Grid2 container spacing={2} columns={12}>
                  <Grid2 size={{ xs: 12, md: 6 }}>
                    {fieldLabel("POD selling price", {
                      iconSrc: RupeeIcon,
                    })}
                    <CustomTextField
                      width="100%"
                      type="number"
                      value={page.podSellingPrice}
                      disabled={!canEdit || page.saving || !isPodEnabled}
                      borderColor={colors.greyBackground}
                      allowDecimalValue
                      onChange={(e) => {
                        page.podSellingPrice = e.target.value;
                      }}
                      placeholder="0"
                    />
                  </Grid2>
                  <Grid2 size={{ xs: 12, md: 6 }}>
                    {fieldLabel("Production time (days)")}
                    <CustomTextField
                      width="100%"
                      type="number"
                      value={page.podProductionTime}
                      disabled={!canEdit || page.saving || !isPodEnabled}
                      borderColor={colors.greyBackground}
                      onChange={(e) => {
                        page.podProductionTime = e.target.value;
                      }}
                      placeholder="e.g. 5"
                    />
                  </Grid2>
                  <Grid2 size={{ xs: 12 }}>
                    <GradientDivider
                      title="Published POD variants"
                      color="128,0,255"
                      sx={{ my: 1 }}
                    />
                    {(catalogProductStore.productPod?.variants ?? []).filter(
                      (pv) => pv.active,
                    ).length === 0 ? (
                      <CustomTypography
                        variant="body2"
                        color={colors.greyTextColor}
                      >
                        No published POD variants yet. Use the POD toggle on
                        each variant in the Variants section.
                      </CustomTypography>
                    ) : (
                      <Box display="flex" flexDirection="column" gap={1}>
                        {(catalogProductStore.productPod?.variants ?? [])
                          .filter((pv) => pv.active)
                          .map((pv) => (
                            <Paper
                              key={pv.id}
                              variant="outlined"
                              sx={{
                                px: 1.5,
                                py: 1,
                                borderRadius: 1.5,
                                borderColor: colors.greyBackground,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                gap: 1,
                                backgroundColor: colors.white,
                              }}
                            >
                              <Box minWidth={0}>
                                <CustomTypography
                                  variant="body2"
                                  fontWeight={600}
                                >
                                  {pv.variant_name ||
                                    `Variant #${pv.variant_id ?? "—"}`}
                                </CustomTypography>
                                <CustomTypography
                                  variant="caption"
                                  color={colors.greyTextColor}
                                >
                                  {[
                                    pv.variant_id
                                      ? `Variant #${pv.variant_id}`
                                      : null,
                                    pv.sku ? `SKU ${pv.sku}` : null,
                                  ]
                                    .filter(Boolean)
                                    .join(" · ")}
                                </CustomTypography>
                              </Box>
                              {canEdit && isPodEnabled && pv.variant_id ? (
                                <Switch
                                  size="small"
                                  color="success"
                                  checked
                                  disabled={page.saving}
                                  onChange={(_, next) => {
                                    if (pv.variant_id && !next) {
                                      void handleTogglePodVariant(
                                        pv.variant_id,
                                        false,
                                      );
                                    }
                                  }}
                                />
                              ) : null}
                            </Paper>
                          ))}
                      </Box>
                    )}
                  </Grid2>
                </Grid2>
              </Box>,
              !isNew && canEdit && isPodEnabled ? (
                <AppButton
                  variant="primary"
                  label="Save POD"
                  onClick={() => {
                    void handleSavePod();
                  }}
                  disabled={page.saving}
                  customStyles={saveButtonStyles}
                />
              ) : null,
            )
          : null}

        {!isNew
          ? sectionShell(
              "variantImages",
              "Variant Images",
              "Per Variant",
              "Upload & manage",
              ImageIcon,
              <ProductVariantImagesEditor
                productId={productId!}
                store={catalogProductStore}
                disabled={!canEdit || page.saving}
                onPreviewImages={(images, initialIndex, title) => {
                  page.openGallery(images, initialIndex, title);
                }}
              />,
            )
          : null}

        {!isNew
          ? sectionShell(
              "media",
              "Media & Assets",
              "Media & Assets",
              "Images & size chart",
              UploadIcon,
              <Grid2 container spacing={2} columns={12}>
                <Grid2
                  size={{ xs: 12 }}
                  display="flex"
                  justifyContent="flex-end"
                  alignItems="flex-end"
                >
                  <AppButton
                    variant="secondary"
                    label="Add images"
                    onClick={() => {
                      document
                        .getElementById("catalog-product-image-input")
                        ?.click();
                    }}
                    disabled={!canEdit || page.saving}
                  />
                  <input
                    id="catalog-product-image-input"
                    type="file"
                    accept="image/*"
                    multiple
                    hidden
                    onChange={handleUploadImages}
                  />
                </Grid2>
                <Grid2 size={{ xs: 12 }}>
                  <Box display="flex" flexWrap="wrap" gap={1.5}>
                    {page.images.map((img, idx) => {
                      const imageKey = `media-${idx}`;
                      return (
                        <Paper
                          key={`${img.image_url}-${idx}`}
                          variant="outlined"
                          sx={{
                            p: 1,
                            width: 200,
                            borderRadius: 1.5,
                            borderColor:
                              img.is_default === 1
                                ? colors.purpleMain
                                : colors.greyBackground,
                            backgroundColor: colors.white,
                          }}
                        >
                          <Box
                            sx={{ position: "relative" }}
                            onMouseEnter={() =>
                              page.setHoveredImageKey(imageKey)
                            }
                            onMouseLeave={() => page.setHoveredImageKey(null)}
                          >
                            <Box
                              component="img"
                              src={img.image_url}
                              alt=""
                              sx={{
                                width: "100%",
                                height: 180,
                                objectFit: "cover",
                                borderRadius: 1,
                                display: "block",
                              }}
                            />
                            {img.image_url &&
                              page.hoveredImageKey === imageKey && (
                                <Box
                                  sx={{
                                    position: "absolute",
                                    top: 0,
                                    left: 0,
                                    width: "100%",
                                    height: "100%",
                                    backgroundColor: "rgba(0, 0, 0, 0.5)",
                                    borderRadius: 1,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    cursor: "pointer",
                                  }}
                                  onClick={(e) => {
                                    try {
                                      e.stopPropagation();
                                      const galleryImages = page.images
                                        .filter((row) => Boolean(row.image_url))
                                        .map((row, i) => ({
                                          id: row.id,
                                          src: row.image_url,
                                          alt:
                                            page.name?.trim() ||
                                            `Product image ${i + 1}`,
                                        }));
                                      const startIndex =
                                        galleryImages.findIndex(
                                          (row) => row.src === img.image_url,
                                        );
                                      page.openGallery(
                                        galleryImages,
                                        startIndex >= 0 ? startIndex : 0,
                                        page.name?.trim() || "Product Images",
                                      );
                                    } catch {
                                      // Ignore; keep media actions usable.
                                    }
                                  }}
                                  aria-label={`View product image ${idx + 1}`}
                                >
                                  <VisibilityOutlinedIcon
                                    sx={{
                                      color: colors?.white || "#fff",
                                      fontSize: 20,
                                    }}
                                  />
                                </Box>
                              )}
                          </Box>
                          <Box
                            mt={0.5}
                            display="flex"
                            justifyContent="space-around"
                          >
                            <Grid2 container columns={6}>
                              <AppButton
                                variant="transparentPrimary"
                                label={
                                  img.is_default === 1
                                    ? "Default"
                                    : "Set default"
                                }
                                onClick={() => {
                                  page.images = page.images.map((row, i) => ({
                                    ...row,
                                    is_default: i === idx ? 1 : 0,
                                  }));
                                }}
                                disabled={!canEdit}
                                customStyles={{ fontSize: 11, minHeight: 28 }}
                              />
                            </Grid2>
                            <Grid2 container columns={6}>
                              <AppButton
                                variant="cancel"
                                label="Remove"
                                onClick={() => {
                                  const removed = page.images[idx];
                                  if (removed?.localFile) {
                                    URL.revokeObjectURL(removed.image_url);
                                  }
                                  page.images = page.images.filter(
                                    (_, i) => i !== idx,
                                  );
                                }}
                                disabled={!canEdit}
                                customStyles={{ fontSize: 11, minHeight: 28 }}
                              />
                            </Grid2>
                          </Box>
                        </Paper>
                      );
                    })}
                  </Box>
                </Grid2>
                <Grid2 size={{ xs: 12 }}>
                  <Box display="flex" alignItems="center" gap={1} mb={2}>
                    <Box
                      component="img"
                      src={NotesIcon}
                      alt=""
                      sx={{ width: 17, height: 17 }}
                    />
                    <CustomTypography variant="heading8">
                      Size Chart
                    </CustomTypography>
                  </Box>
                  <MediaAssetsUpload
                    key={`size-chart-${
                      page.sizeChartUrl ? "has-image" : "no-image"
                    }-${page.selectedSizeChartFile ? "has-file" : "no-file"}`}
                    maxWidth="100%"
                    uploadText="Upload size chart"
                    fileInfo="PNG, JPG up to 2MB each"
                    multiple={false}
                    isViewOnly={!canEdit || page.saving}
                    existingImageList={
                      page.selectedSizeChartFile
                        ? []
                        : page.sizeChartUrl
                          ? [
                              {
                                img: page.sizeChartUrl,
                                title: "Size Chart",
                              },
                            ]
                          : []
                    }
                    newImageList={
                      page.selectedSizeChartFile
                        ? [page.selectedSizeChartFile]
                        : []
                    }
                    deleteImage={() => {
                      try {
                        page.removeSizeChartImage();
                        page.clearSelectedSizeChartFile();
                      } catch {
                        showError(
                          "Error",
                          "Unable to remove size chart. Please try again.",
                        );
                      }
                    }}
                    removeImage={() => {
                      try {
                        page.removeSizeChartImage();
                        page.clearSelectedSizeChartFile();
                      } catch {
                        showError(
                          "Error",
                          "Unable to remove size chart. Please try again.",
                        );
                      }
                    }}
                    addImagesToBeUploaded={(files: File[]) => {
                      try {
                        if (files.length > 0) {
                          page.removeSizeChartImage();
                          page.setSelectedSizeChartFile(files[0]);
                        }
                      } catch {
                        showError(
                          "Error",
                          "Unable to select size chart. Please try again.",
                        );
                      }
                    }}
                  />
                </Grid2>
              </Grid2>,
              canEdit ? (
                <AppButton
                  variant="primary"
                  label="Save Media"
                  onClick={() => {
                    void handleSaveMedia();
                  }}
                  disabled={page.saving}
                  customStyles={saveButtonStyles}
                />
              ) : null,
            )
          : null}

        {!isNew
          ? sectionShell(
              "psd",
              "PSD Configuration",
              "Mockups",
              "Placement templates",
              EyeIcon,
              <Grid2 container spacing={2} columns={12}>
                <Grid2 size={{ xs: 12 }}>
                  <PodProductPsdMappingsCard
                    embedded
                    productId={productId!}
                    skuChoices={activeCatalogSkus(
                      catalogProductStore.productDetail?.variants ?? [],
                    )}
                    loadingOptions={psdLoadingOptions}
                    uploading={psdUploading}
                    onUploadPsd={handleUploadPodPsd}
                    onSetDefault={handleSetDefaultPsdTemplate}
                    onRemove={handleRemovePsdTemplate}
                    onDownload={handleDownloadPsdTemplate}
                  />
                </Grid2>
              </Grid2>,
            )
          : null}

        {!isNew
          ? sectionShell(
              "sourcing",
              "Sourcing Details",
              "Sourcing",
              "Vendors & tax",
              SourcingIcon,
              <Grid2 container spacing={2} columns={12}>
                <Grid2 size={{ xs: 12, md: 4 }}>
                  {fieldLabel("Zoho ID", { iconSrc: TagIcon })}
                  <CustomTextField
                    width="100%"
                    value={page.zohoId}
                    disabled={!canEditSourcing || page.saving}
                    borderColor={colors.greyBackground}
                    onChange={(e) => {
                      page.zohoId = e.target.value;
                    }}
                  />
                </Grid2>
                <Grid2 size={{ xs: 12, md: 4 }}>
                  {fieldLabel("HSN code")}
                  <CustomTextField
                    width="100%"
                    value={page.hsnCode}
                    disabled={!canEditSourcing || page.saving}
                    borderColor={colors.greyBackground}
                    onChange={(e) => {
                      page.hsnCode = e.target.value;
                    }}
                  />
                </Grid2>
                <Grid2 size={{ xs: 12, md: 4 }}>
                  {fieldLabel("GST %")}
                  <CustomTextField
                    width="100%"
                    value={page.gstPercentage}
                    disabled={!canEditSourcing || page.saving}
                    borderColor={colors.greyBackground}
                    onChange={(e) => {
                      page.gstPercentage = e.target.value;
                    }}
                  />
                </Grid2>
                <Grid2 size={{ xs: 12, md: 6 }}>
                  {flagRow(
                    "Fast procurement",
                    page.fastProcurement === 1,
                    (n) => page.setFastProcurement(n ? 1 : 0),
                    !canEditSourcing,
                  )}
                </Grid2>
                <Grid2 size={{ xs: 12, md: 6 }}>
                  {fieldLabel("Preferred vendor", { iconSrc: BusinessIcon })}
                  <AppSearchDropdownMulti
                    title="Select vendor"
                    isMultiSelect={false}
                    local
                    disabled={!canEditSourcing || page.saving}
                    list={page.vendorList}
                    value={
                      page.preferredVendorId
                        ? [
                            {
                              id: String(page.preferredVendorId),
                              value:
                                page.preferredVendorName ||
                                `Vendor #${page.preferredVendorId}`,
                            },
                          ]
                        : []
                    }
                    handleSearch={(term) => {
                      page.vendorSearch = term || "";
                    }}
                    handleSelect={(items) => {
                      const next = items[0];
                      if (!next) {
                        page.preferredVendorId = null;
                        page.preferredVendorName = "";
                        return;
                      }
                      page.preferredVendorId = parseInt(String(next.id), 10);
                      page.preferredVendorName = next.value;
                      if (!page.vendorIds.includes(page.preferredVendorId)) {
                        page.vendorIds = [
                          ...page.vendorIds,
                          page.preferredVendorId,
                        ];
                      }
                    }}
                  />
                </Grid2>
                <Grid2 size={{ xs: 12, md: 6 }}>
                  {fieldLabel("Vendors", { iconSrc: BusinessIcon })}
                  <AppSearchDropdownMulti
                    title="Select vendors"
                    isMultiSelect
                    local
                    disabled={!canEditSourcing || page.saving}
                    list={page.vendorList}
                    value={page.vendorList.filter((v) =>
                      page.vendorIds.includes(parseInt(String(v.id), 10)),
                    )}
                    handleSearch={(term) => {
                      page.vendorSearch = term || "";
                    }}
                    handleSelect={(items) => {
                      page.vendorIds = items
                        .map((i) => parseInt(String(i.id), 10))
                        .filter((n) => Number.isFinite(n) && n > 0);
                    }}
                  />
                </Grid2>
                <Grid2 size={{ xs: 12, md: 6 }}>
                  {fieldLabel("Preferred printing type")}
                  <AppSearchDropdownMulti
                    title="Select printing type"
                    isMultiSelect={false}
                    local
                    disabled={!canEditPrinting || page.saving}
                    list={page.printingTypeList}
                    value={
                      page.preferredPrintingTypeId
                        ? [
                            {
                              id: String(page.preferredPrintingTypeId),
                              value:
                                page.preferredPrintingTypeName ||
                                `Type #${page.preferredPrintingTypeId}`,
                            },
                          ]
                        : []
                    }
                    handleSelect={(items) => {
                      const next = items[0];
                      if (!next) {
                        page.preferredPrintingTypeId = null;
                        page.preferredPrintingTypeName = "";
                        return;
                      }
                      page.preferredPrintingTypeId = parseInt(
                        String(next.id),
                        10,
                      );
                      page.preferredPrintingTypeName = next.value;
                      if (
                        !page.printingTypeIds.includes(
                          page.preferredPrintingTypeId,
                        )
                      ) {
                        page.printingTypeIds = [
                          ...page.printingTypeIds,
                          page.preferredPrintingTypeId,
                        ];
                      }
                    }}
                  />
                </Grid2>
                <Grid2 size={{ xs: 12, md: 6 }}>
                  {fieldLabel("Printing types")}
                  <AppSearchDropdownMulti
                    title="Select printing types"
                    isMultiSelect
                    local
                    disabled={!canEditPrinting || page.saving}
                    list={page.printingTypeList}
                    value={page.printingTypeList.filter((v) =>
                      page.printingTypeIds.includes(parseInt(String(v.id), 10)),
                    )}
                    handleSelect={(items) => {
                      page.printingTypeIds = items
                        .map((i) => parseInt(String(i.id), 10))
                        .filter((n) => Number.isFinite(n) && n > 0);
                    }}
                  />
                </Grid2>
                <Grid2 size={{ xs: 12 }}>
                  <Box
                    sx={{
                      borderRadius: 3,
                      border: `1px solid ${colors.greyBackground}`,
                      backgroundColor: colors.white,
                      overflow: "hidden",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: { xs: "flex-start", sm: "center" },
                        gap: 2,
                        px: { xs: 2, md: 3 },
                        py: 2,
                        backgroundColor: colors.subtleOverlay,
                        borderBottom: `1px solid ${colors.greyBackground}`,
                        cursor: "pointer",
                      }}
                      onClick={() => setPurchaseInsightsOpen((prev) => !prev)}
                    >
                      <Box display="flex" alignItems="flex-start" gap={1}>
                        <img
                          src={StatusIcon}
                          alt="Previous Procurement"
                          style={{ width: 24, height: 24 }}
                        />
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 0.2,
                          }}
                        >
                          <CustomTypography variant="heading7">
                            Previous Procurement Insights
                          </CustomTypography>
                          <CustomTypography
                            variant="body3"
                            color="text.secondary"
                          >
                            Review historical purchase requests & negotiated
                            prices
                          </CustomTypography>
                        </Box>
                      </Box>
                      <IconButton
                        size="small"
                        onClick={(event) => {
                          event.stopPropagation();
                          setPurchaseInsightsOpen((prev) => !prev);
                        }}
                      >
                        {purchaseInsightsOpen ? (
                          <ExpandLess fontSize="small" />
                        ) : (
                          <ExpandMore fontSize="small" />
                        )}
                      </IconButton>
                    </Box>

                    <Collapse in={purchaseInsightsOpen} timeout="auto">
                      <Box sx={{ px: { xs: 1.5, md: 3 }, py: 3 }}>
                        {catalogProductStore.purchaseRequestInsights.length ===
                          0 && (
                          <CustomTypography
                            variant="body2"
                            color="text.secondary"
                          >
                            No purchase request insights found.
                          </CustomTypography>
                        )}
                        {catalogProductStore.purchaseRequestInsights.length >
                          0 &&
                          catalogProductStore.purchaseRequestInsights.map(
                            (insight, index) => {
                              const rowBg =
                                index % 2 === 0
                                  ? colors.lightBlue
                                  : colors.greyBackground;
                              const totalValue =
                                insight.quantity * insight.rate;

                              return (
                                <Box
                                  key={`${insight.pr_id}-${insight.po_id}`}
                                  sx={{
                                    display: "grid",
                                    gridTemplateColumns: {
                                      xs: "1fr",
                                      sm: "repeat(2, 1fr)",
                                      lg: "1.2fr 0.9fr 0.9fr 0.8fr",
                                    },
                                    gap: { xs: 1.75, md: 2 },
                                    backgroundColor: rowBg,
                                    borderRadius: 2,
                                    px: { xs: 2, md: 3 },
                                    py: { xs: 2, md: 1.5 },
                                    mb:
                                      index ===
                                      catalogProductStore
                                        .purchaseRequestInsights.length -
                                        1
                                        ? 0
                                        : 2,
                                    border: `1px solid ${colors.lightBorder}`,
                                  }}
                                >
                                  <Box
                                    sx={{
                                      display: "flex",
                                      flexDirection: "column",
                                      gap: 0.2,
                                    }}
                                  >
                                    <CustomTypography
                                      variant="body3"
                                      color="text.secondary"
                                      sx={{ textTransform: "uppercase" }}
                                    >
                                      Purchase Request
                                    </CustomTypography>
                                    <CustomTypography
                                      variant="body2"
                                      fontWeight={600}
                                    >
                                      {insight.title}
                                    </CustomTypography>
                                    <CustomTypography
                                      variant="body3"
                                      color="text.secondary"
                                    >
                                      PR #{insight.pr_id} · PO #{insight.po_id}
                                    </CustomTypography>
                                  </Box>

                                  <Box
                                    sx={{
                                      display: "flex",
                                      flexDirection: "column",
                                      gap: 0.2,
                                    }}
                                  >
                                    <CustomTypography
                                      variant="body3"
                                      color="text.secondary"
                                      sx={{ textTransform: "uppercase" }}
                                    >
                                      Customer
                                    </CustomTypography>
                                    <CustomTypography
                                      variant="body2"
                                      fontWeight={600}
                                    >
                                      {insight.customer_name}
                                    </CustomTypography>
                                    <CustomTypography
                                      variant="body3"
                                      color="text.secondary"
                                    >
                                      Order #{insight.order_id}
                                    </CustomTypography>
                                  </Box>

                                  <Box
                                    sx={{
                                      display: "flex",
                                      flexDirection: "column",
                                      gap: 0.2,
                                    }}
                                  >
                                    <CustomTypography
                                      variant="body3"
                                      color="text.secondary"
                                      sx={{ textTransform: "uppercase" }}
                                    >
                                      Procured Qty & Rate
                                    </CustomTypography>
                                    <CustomTypography
                                      variant="body2"
                                      fontWeight={600}
                                      sx={{ display: "flex", gap: 0.5 }}
                                    >
                                      {insight.quantity} units ·{" "}
                                      {formatAmount(insight.rate)}
                                    </CustomTypography>
                                    <Chip
                                      label={`${formatAmount(totalValue)} total`}
                                      size="small"
                                      sx={{
                                        mt: 0.5,
                                        alignSelf: "flex-start",
                                        backgroundColor: colors.white,
                                        color: colors.blackBorder,
                                        borderRadius: "12px",
                                        border: `1px solid ${colors.greyBorder}`,
                                        fontWeight: 600,
                                      }}
                                    />
                                  </Box>

                                  <Box
                                    sx={{
                                      display: "flex",
                                      flexDirection: "column",
                                      gap: 0.2,
                                    }}
                                  >
                                    <CustomTypography
                                      variant="body3"
                                      color="text.secondary"
                                      sx={{ textTransform: "uppercase" }}
                                    >
                                      Processed By
                                    </CustomTypography>
                                    <CustomTypography
                                      variant="body2"
                                      fontWeight={600}
                                    >
                                      {insight.created_by_admin_name}
                                    </CustomTypography>
                                    <CustomTypography
                                      variant="body3"
                                      color="text.secondary"
                                    >
                                      {formatDate(insight.created_date)}
                                    </CustomTypography>
                                  </Box>
                                </Box>
                              );
                            },
                          )}
                      </Box>
                    </Collapse>
                  </Box>
                </Grid2>
              </Grid2>,
              canEditSourcing ? (
                <AppButton
                  variant="primary"
                  label="Save Sourcing"
                  onClick={() => {
                    void handleSaveSourcing();
                  }}
                  disabled={page.saving}
                  customStyles={saveButtonStyles}
                />
              ) : null,
            )
          : null}

        {!isNew
          ? sectionShell(
              "categories",
              "Additional Categories",
              "Mappings",
              "Secondary",
              StatusIcon,
              <Box>
                <CustomTypography
                  variant="body2"
                  color={colors.greyTextColor}
                  sx={{ mb: 2 }}
                >
                  Primary category is set above. Add secondary category mappings
                  here.
                </CustomTypography>
                <AppSearchDropdownMulti
                  title="Additional categories"
                  isMultiSelect
                  local
                  disabled={!canEdit || page.saving}
                  list={page.categoryList}
                  value={page.categoryList.filter((c) =>
                    page.extraCategoryIds.includes(parseInt(String(c.id), 10)),
                  )}
                  handleSearch={(term) => {
                    page.categorySearch = term || "";
                  }}
                  handleSelect={(items) => {
                    page.extraCategoryIds = items
                      .map((i) => parseInt(String(i.id), 10))
                      .filter(
                        (n) =>
                          Number.isFinite(n) && n > 0 && n !== page.categoryId,
                      );
                  }}
                />
              </Box>,
              canEdit ? (
                <AppButton
                  variant="primary"
                  label="Save Categories"
                  onClick={() => {
                    void handleSaveCategories();
                  }}
                  disabled={page.saving}
                  customStyles={saveButtonStyles}
                />
              ) : null,
            )
          : null}

        {!isNew
          ? sectionShell(
              "seo",
              "SEO Details",
              "Search",
              "2 fields",
              SearchIcon,
              <Grid2 container spacing={2} columns={12}>
                <Grid2 size={{ xs: 12 }}>
                  {fieldLabel("SEO title", { required: true })}
                  <CustomTextField
                    width="100%"
                    value={page.seoTitle}
                    disabled={!canEditSeo || page.saving}
                    borderColor={colors.greyBackground}
                    onChange={(e) => {
                      page.seoTitle = e.target.value;
                    }}
                  />
                </Grid2>
                <Grid2 size={{ xs: 12 }}>
                  {fieldLabel("SEO description", { required: true })}
                  <CustomTextField
                    width="100%"
                    placeholder="SEO description"
                    value={page.seoDescription}
                    disabled={!canEditSeo || page.saving}
                    borderColor={colors.greyBackground}
                    onChange={(e) => {
                      page.seoDescription = e.target.value;
                    }}
                  />
                </Grid2>
              </Grid2>,
              canEditSeo ? (
                <AppButton
                  variant="primary"
                  label="Save SEO"
                  onClick={() => {
                    void handleSaveSeo();
                  }}
                  disabled={page.saving}
                  customStyles={saveButtonStyles}
                />
              ) : null,
            )
          : null}
      </Box>

      {page.deleteOpen && detail ? (
        <ConfirmationPopup
          id={detail.id}
          open={page.deleteOpen}
          title={detail.active ? "Deactivate Product" : "Restore Product"}
          content={
            detail.active
              ? `Soft-delete "${detail.name}"?`
              : `Restore "${detail.name}"?`
          }
          onConfirm={() => {
            void handleToggleActive();
          }}
          onCancel={() => {
            page.deleteOpen = false;
          }}
        />
      ) : null}

      <ImageGalleryPopup
        key="product-detail-images"
        open={page.galleryOpen}
        images={page.galleryImages}
        title={page.galleryTitle}
        initialIndex={page.galleryInitialIndex}
        onClose={() => page.closeGallery()}
      />

      <ProductActivityLogsDrawer
        open={activityLogsDrawerOpen}
        onClose={() => setActivityLogsDrawerOpen(false)}
        productId={productId}
        catalogProductStore={catalogProductStore}
      />
    </Box>
  );
};

export default observer(ProductDetail);
